import React, { useContext } from 'react';
import { Link, useLoaderData, useSearchParams, Outlet, redirect } from 'react-router-dom';
import { ContentFrame } from '../../components/content_frame/content_frame';
import { 
  PATIENT_LIST_PATH, 
  PATIENT_ADD_PATH, 
  PATIENT_EDIT_PATH, 
  DEFAULT_DELETE_PATH, 
  DESKTOP_PATH,
  LOGIN_PATH } from '../../constants/paths';
import { CLINICS_GET_API, PATIENTS_GET_API } from '../../constants/apis';
import DefaultInput, { HalfWrapper, SelectOption } from '../../components/default_input/default_input';
import { AuthContext, ClinicListContext } from '../../contexts';
import ModelTable, { Property } from '../../components/model_table/model_table';
import DefaultSection from '../../components/default_section/default_section';
import { getModels, getAsLocalDatetime, setUrlParameters } from '../../utils/functions';
import { ADD_PATIENT_PERMISSION, EDIT_PATIENT_PERMISSION, DELETE_PATIENT_PERMISSION } from '../../constants/permissions';
import './patient_list.scss';


export async function patientListRootLoader() {
  try {
    return await getModels(setUrlParameters(CLINICS_GET_API, {is_active: true}));
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

export const PatientListRoot = props => {
  const clinics = useLoaderData();  

  return (
    <ClinicListContext.Provider value={clinics}>

      <Outlet />

    </ClinicListContext.Provider>
  );
};

export async function patientListLoader({request}) { 
  const url = new URL(request.url);
  
  const parameters = {
    is_active: url.searchParams.get("is_active"),
    clinic_id: url.searchParams.get("clinic_id"),
    load_clinic_name: true
  };

  if(parameters.is_active === 'all') {
    parameters.is_active = null;
  }
  else if (parameters.is_active === null) {
    parameters.is_active = true;
  }

  try {
    return await getModels(setUrlParameters(PATIENTS_GET_API, parameters));
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

class PatientList extends React.Component {
  constructor(props) {
    super(props);

    let isActiveInput = this.props.searchParams.get('is_active');
    let clinicIdInput = this.props.searchParams.get('clinic_id');

    if(!isActiveInput) {
      isActiveInput = 'true';
    }
    else if(isActiveInput === 'all') {
      isActiveInput = '';
    }

    if(!clinicIdInput) {
      clinicIdInput = '';
    }

    this.state = {
      isActiveInput: isActiveInput,
      isActiveFilter: isActiveInput,
      clinicIdInput: clinicIdInput,
      clinicIdFilter: clinicIdInput,
      screenWidth: window.innerWidth
    };
  }

  async componentDidMount() {
    this.resizeListener = () => this.updateSize();

    window.addEventListener("resize", this.resizeListener);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resizeListener);
  }

  updateSize() {
    this.setState({
      screenWidth: window.innerWidth
    });
  }

  getCreatedAtText(entry) {
    return (getAsLocalDatetime(entry.created_at, false).toLocaleDateString())
  }

  listHasActions() {
    return this.props.auth.permission_ids.includes(EDIT_PATIENT_PERMISSION) ||
           this.props.auth.permission_ids.includes(DELETE_PATIENT_PERMISSION);
  }

  getActions(entry) {
    return (
      <div className="model-table__model-actions-container">
      
        {this.props.auth.permission_ids.includes(EDIT_PATIENT_PERMISSION) &&
          <Link
            className="model-table__default-edit-button"
            to={`${PATIENT_EDIT_PATH}${entry.id}`}
          >

              <i className="fas fa-edit"></i>

          </Link>
        }
        
        {this.props.auth.permission_ids.includes(DELETE_PATIENT_PERMISSION) &&
          <Link
            className="model-table__default-delete-button"
            to={`${DEFAULT_DELETE_PATH.slice(1)}${entry.id}`}
          >

              <i className="far fa-trash-alt"></i>

          </Link>
        }

      </div>
    );
  }

  getIsActiveFilterText(entry) {
    let activeText;

    if(entry.is_active) {
      activeText = 'Ativo';
    }
    else {
      activeText = 'Inativo';
    }

    return activeText;
  }

  getIsActiveText(entry) {
    let activeText;
    let classModifier;

    if(entry.is_active) {
      activeText = 'Ativo';
      classModifier = 'ativo';
    }
    else {
      activeText = 'Inativo';
      classModifier = 'inativo';
    }

    return (
      <p className={`patient-list__is-active-text__${classModifier}`}>{activeText}</p>
    );
  }

  getProperties() {
    let properties = [
      Property('name', 'Nome', <i className="fa-solid fa-tag"></i>),
      Property('cpf', 'CPF', <i className="fa-solid fa-id-card"></i>),
      Property('clinic_name', 'Cliente', <i className="fa-solid fa-tooth"></i>)
    ];

    if(this.state.screenWidth > 550) {
      properties.push(
        Property('created_at', 'Cadastro', <i className="fas fa-calendar-alt"></i>, {getDataText: this.getCreatedAtText, getFilterText: this.getCreatedAtText})
      );
    }

    properties = [
      ...properties,
      Property('is_active', 'Situação', <i className="fas fa-thermometer-half"></i>, {getDataText: this.getIsActiveText, getFilterText: this.getIsActiveFilterText})
    ];

    return properties;
  }

  getActiveOptions() {
    return [
      SelectOption('true', 'Ativo'),
      SelectOption('false', 'Inativo'),
      SelectOption('', 'Todos'),
    ];
  }

  getClinicOptions() {
    return [
      SelectOption('', 'Todas'),
      ...this.props.clinics.map((clinic) => SelectOption(clinic.id, clinic.name))
    ];
  }

  mayResetFilterInputs() {
    if(this.state.isActiveInput.length > 0 ||
       this.state.clinicIdInput.length > 0
    ) {
      return true;
    }

    return false;
  }

  resetFilterInputs() {
    this.setState({
      isActiveInput: '',
      clinicIdInput: '',
    });
  }

  mayRefreshList() {
    if(this.state.isActiveInput !== this.state.isActiveFilter ||
       this.state.clinicIdInput !== this.state.clinicIdFilter 
    ) {
      return true;
    }

    return false;
  }

  async refreshList() {
    if(this.mayRefreshList()) {
      
      this.props.setSearchParams(setUrlParameters("", {
        is_active: this.state.isActiveInput || 'all',
        clinic_id: this.state.clinicIdInput
      }), {
        replace: true
      });      

      this.setState({
        isActiveFilter: this.state.isActiveInput,
        clinicIdFilter: this.state.clinicIdInput
      });
    }
  }

  handleInputChange(event) {
    const target = event.target;
    let value = target.value;
    let name = target.name;

    const update = {[name]: value};

    this.setState(update);
  }

  render() {
    return (
      <>
        
        <Outlet />

        <ContentFrame
          headerHistory={[
            {
              path: DESKTOP_PATH,
              text: "Área de trabalho"
            },
            {
              path: PATIENT_LIST_PATH,
              text: "Listar pacientes"
            },
          ]}
          titleIcon={<i className="fas fa-clipboard-list"></i>}
          title="Listar pacientes"
        >

          <DefaultSection
            className="patient-list"
            title="Lista de pacientes"
          >

            <div className="patient-list__list-actions">

              {this.props.auth.permission_ids.includes(ADD_PATIENT_PERMISSION) &&
                <Link
                  className="model-table__default-button"
                  to={PATIENT_ADD_PATH}
                >

                  <i className="fas fa-plus"></i> Adicionar paciente

                </Link>
              }

            </div>

            <div className="patient-list__filters">

              <header className="patient-list__filters__header">

                <h4 className="patient-list__filters__header__text">Filtros</h4>

              </header>

              <div className="patient-list__filters__inputs">

                <div className="patient-list__filters__inputs-wrapper">

                  <HalfWrapper>

                    <DefaultInput
                      name="isActiveInput"
                      label="Situação do cadastro"
                      type="select"
                      handleInputChange={(event) => this.handleInputChange(event)}
                      value={this.state.isActiveInput || ''}
                      options={this.getActiveOptions()}
                    />

                    <DefaultInput
                      name="clinicIdInput"
                      label="Clínica"
                      labelMessage="Lista de cadastros ativos"
                      type="select"
                      handleInputChange={(event) => this.handleInputChange(event)}
                      value={this.state.clinicIdInput || ''}
                      options={this.getClinicOptions()}
                    />

                  </HalfWrapper>


                </div>

                {this.mayResetFilterInputs() &&
                  <button
                    className="patient-list__filters__reset-button"
                    onClick={() => this.resetFilterInputs()}
                  >

                    <i className="fas fa-times"></i>

                  </button>
                }

                {this.mayRefreshList() &&
                  <button
                    className="patient-list__filters__refresh-button"
                    onClick={() => this.refreshList()}
                  >

                    <i className="fas fa-sync"></i>

                  </button>
                }

              </div>

            </div>

            <ModelTable
              properties={this.getProperties()}
              getActions={this.listHasActions() ? (entry) => this.getActions(entry) : null}
              data={this.props.patients}
              initialOrderBy="name"
            >

            </ModelTable>

          </DefaultSection>

        </ContentFrame>

      </>
    );
  }
};

const WrappedPatientList = props => {
  const patients = useLoaderData();
  const clinics = useContext(ClinicListContext);
  const auth = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();  

  return (
    <PatientList
      clinics={clinics}
      patients={patients}
      auth={auth}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
      {...props}
    />
  );
};

export {WrappedPatientList as PatientList};
